import { useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import * as XLSX from "xlsx";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { UPLOAD_TYPES } from "../data";
import { Panel, outlinedButtonSx, primaryButtonSx } from "../sharedStyles";

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls", ".csv"];
const MAX_PREVIEW_ROWS = 30;

const normalizeHeader = (value) =>
	String(value || "")
		.trim()
		.toUpperCase()
		.replace(/\s+/g, " ")
		.replace(/_/g, "-");

const HEADER_ALIASES = {
	"CUSTOMER NAME": "customer_name",
	CUSTOMER: "customer_name",
	LOCATION: "location",
	DATE: "date",
	"EAN CODE": "ean_code",
	EAN: "ean_code",
	"EAN-CODE": "ean_code",
	QTY: "qty",
	QUANTITY: "qty",
	"DOCUMENT NO": "document_no",
	"DOCUMENT-NO": "document_no",
	DOCUMENTNO: "document_no",
	"DOC NO": "document_no",
	"DOC-NO": "document_no",
};

const formatCell = (key, value) => {
	if (value === null || value === undefined || value === "") return "—";
	if (key === "qty") return Number(value).toLocaleString("en-IN");
	return String(value);
};

const excelSerialToIso = (value) => {
	const date = new Date(Date.UTC(1899, 11, 30) + Math.round(value) * 86400000);
	return date.toISOString().slice(0, 10);
};

const eanText = (value) => {
	if (value === null || value === undefined || value === "") return "";
	if (typeof value === "number" && Number.isFinite(value)) return Math.round(value).toString();
	const text = String(value).trim();
	if (/^[+-]?\d+(\.\d+)?e[+-]?\d+$/i.test(text)) {
		const num = Number(text);
		if (Number.isFinite(num)) return Math.round(num).toString();
	}
	return text.replace(/\.0+$/, "");
};

const parseFileForType = async (file, uploadType) => {
	const buffer = await file.arrayBuffer();
	const workbook = XLSX.read(buffer, { type: "array" });
	const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
	const rawRows = XLSX.utils.sheet_to_json(firstSheet, {
		header: 1,
		defval: "",
		blankrows: false,
		raw: true,
	});

	if (!rawRows.length) throw new Error("File is empty");

	const headers = rawRows[0];
	const indexToKey = {};
	headers.forEach((header, index) => {
		const key = HEADER_ALIASES[normalizeHeader(header)];
		if (key) indexToKey[index] = key;
	});

	const foundKeys = new Set(Object.values(indexToKey));
	const missing = uploadType.columns
		.filter((col) => col.required && !foundKeys.has(col.key))
		.map((col) => col.label);

	const rows = rawRows
		.slice(1)
		.map((row, rowIndex) => {
			const mapped = { id: rowIndex + 1 };
			Object.entries(indexToKey).forEach(([colIndex, key]) => {
				const cell = row[Number(colIndex)];
				if (key === "qty") {
					const cleaned = String(cell ?? "").replace(/,/g, "").trim();
					mapped[key] = cleaned === "" ? null : Number(cleaned);
				} else if (key === "ean_code") {
					mapped[key] = eanText(cell);
				} else if (key === "date" && typeof cell === "number") {
					mapped[key] = excelSerialToIso(cell);
				} else {
					mapped[key] = String(cell ?? "").trim();
				}
			});
			return mapped;
		})
		.filter((row) => row.customer_name || row.ean_code);

	return { rows, missing };
};

const formatUploadAt = (value) => {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return date.toLocaleString("en-IN", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

function UploadCard({ uploadType, state, lastUpload, onFile, onClear, onUpload, onDownloadTemplate }) {
	const inputRef = useRef(null);
	const [isDragOver, setIsDragOver] = useState(false);
	const { file, rows, missing, uploading, uploaded, error } = state;

	useEffect(() => {
		if (!file && inputRef.current) inputRef.current.value = "";
	}, [file]);

	const handleDrop = (e) => {
		e.preventDefault();
		setIsDragOver(false);
		onFile(uploadType.key, e.dataTransfer?.files?.[0]);
	};

	return (
		<Panel
			sx={{
				p: 2,
				height: "100%",
				minWidth: 0,
				width: "100%",
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<Stack direction="row" spacing={1.25} alignItems="flex-start" mb={1.5}>
				<Box
					sx={{
						width: 36,
						height: 36,
						borderRadius: 1.5,
						display: "grid",
						placeItems: "center",
						bgcolor: "#f3f4f6",
						color: "#1f2937",
						flexShrink: 0,
						"& .MuiSvgIcon-root": { fontSize: 20 },
					}}
				>
					{uploadType.icon}
				</Box>
				<Box minWidth={0} flex={1}>
					<Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
						<Typography variant="subtitle2" fontWeight={700} color="#111827">
							{uploadType.name}
						</Typography>
						{uploadType.group && (
							<Chip
								size="small"
								label={uploadType.group}
								sx={{
									height: 20,
									fontSize: "0.65rem",
									fontWeight: 700,
									letterSpacing: 0.2,
									bgcolor: "#111827",
									color: "#fff",
								}}
							/>
						)}
					</Stack>
					<Typography variant="caption" color="text.secondary" display="block">
						{uploadType.description}
					</Typography>
					{lastUpload?.uploaded_at && (
						<Typography variant="caption" fontWeight={600} color="#1f2937" display="block">
							Latest upload {formatUploadAt(lastUpload.uploaded_at)}
						</Typography>
					)}
				</Box>
				{uploaded && (
					<Chip
						size="small"
						icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
						label="Uploaded"
						sx={{
							height: 24,
							fontWeight: 600,
							fontSize: "0.7rem",
							bgcolor: "#f0fdf4",
							color: "#15803d",
						}}
					/>
				)}
			</Stack>

			<Stack direction="row" flexWrap="wrap" gap={0.5} mb={1.5}>
				{uploadType.columns.map((col) => (
					<Chip
						key={col.key}
						size="small"
						label={col.label}
						sx={{
							height: 22,
							fontSize: "0.65rem",
							fontWeight: 600,
							bgcolor: "#f9fafb",
							border: "1px solid #e5e7eb",
							color: "#374151",
						}}
					/>
				))}
			</Stack>

			<Box
				onDragOver={(e) => {
					e.preventDefault();
					setIsDragOver(true);
				}}
				onDragLeave={() => setIsDragOver(false)}
				onDrop={handleDrop}
				onClick={() => !uploading && inputRef.current?.click()}
				sx={{
					border: "1.5px dashed",
					borderColor: isDragOver
						? "#1f2937"
						: file
							? "rgba(21, 128, 61, 0.45)"
							: "#d1d5db",
					borderRadius: 1.5,
					px: 1.5,
					py: 2,
					textAlign: "center",
					cursor: uploading ? "not-allowed" : "pointer",
					bgcolor: isDragOver
						? "rgba(31, 41, 55, 0.04)"
						: file
							? "rgba(21, 128, 61, 0.04)"
							: "#fafafa",
					mb: 1.5,
					flex: 1,
				}}
			>
				<input
					ref={inputRef}
					type="file"
					hidden
					accept=".xlsx,.xls,.csv"
					onChange={(e) => onFile(uploadType.key, e.target.files?.[0])}
				/>
				{file ? (
					<Stack spacing={0.5} alignItems="center">
						<InsertDriveFileOutlinedIcon sx={{ color: "#15803d", fontSize: 22 }} />
						<Typography
							variant="caption"
							fontWeight={700}
							color="#111827"
							sx={{ wordBreak: "break-all" }}
						>
							{file.name}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{rows.length} rows
						</Typography>
					</Stack>
				) : (
					<Stack spacing={0.35} alignItems="center">
						<CloudUploadOutlinedIcon sx={{ color: "#9ca3af", fontSize: 24 }} />
						<Typography variant="caption" fontWeight={600} color="#374151">
							Drop file or browse
						</Typography>
						<Typography variant="caption" color="text.secondary">
							.xlsx / .xls / .csv
						</Typography>
					</Stack>
				)}
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 1.25, py: 0, borderRadius: 1.5 }}>
					{error}
				</Alert>
			)}
			{missing.length > 0 && (
				<Alert severity="warning" sx={{ mb: 1.25, py: 0, borderRadius: 1.5 }}>
					Missing: {missing.join(", ")}
				</Alert>
			)}

			<Stack
				direction="row"
				spacing={0.75}
				flexWrap="wrap"
				useFlexGap
				sx={{ mt: "auto", width: "100%", minWidth: 0 }}
			>
				<Button
					size="small"
					variant="outlined"
					startIcon={<DownloadOutlinedIcon />}
					onClick={() => onDownloadTemplate(uploadType)}
					sx={{ ...outlinedButtonSx, height: 32, fontSize: "0.75rem" }}
				>
					Template
				</Button>
				{file && (
					<Button
						size="small"
						variant="outlined"
						startIcon={<DeleteOutlineIcon />}
						onClick={() => onClear(uploadType.key)}
						sx={{ ...outlinedButtonSx, height: 32, fontSize: "0.75rem" }}
					>
						Clear
					</Button>
				)}
				<Button
					size="small"
					variant="contained"
					disabled={!file || missing.length > 0 || uploading || !rows.length}
					onClick={() => onUpload(uploadType.key)}
					startIcon={
						uploading ? (
							<CircularProgress size={12} color="inherit" />
						) : (
							<CloudUploadOutlinedIcon />
						)
					}
					sx={{ ...primaryButtonSx, height: 32, fontSize: "0.75rem" }}
				>
					{uploading ? "Uploading..." : "Upload"}
				</Button>
			</Stack>
		</Panel>
	);
}

const emptyState = () => ({
	file: null,
	rows: [],
	missing: [],
	uploading: false,
	uploaded: false,
	error: "",
});

function Upload() {
	const { setSnackBar, setSnackBarOpen } = GlobalState();
	const showSnack = (message, severity = "info") => {
		setSnackBarOpen(true);
		setSnackBar((prev) => ({ ...prev, message, severity }));
	};

	const [states, setStates] = useState(() =>
		Object.fromEntries(UPLOAD_TYPES.map((type) => [type.key, emptyState()]))
	);
	const [activePreviewKey, setActivePreviewKey] = useState(UPLOAD_TYPES[0].key);
	const [latestUploads, setLatestUploads] = useState({});

	const loadLatestUploads = async () => {
		try {
			const res = await pythonVmInstance.get("/mbo-lfs-qty-reco/uploads");
			setLatestUploads(res?.data?.result?.uploads || {});
		} catch {
			setLatestUploads({});
		}
	};

	useEffect(() => {
		loadLatestUploads();
	}, []);

	const updateState = (key, patch) => {
		setStates((prev) => ({
			...prev,
			[key]: { ...prev[key], ...patch },
		}));
	};

	const handleFile = async (typeKey, file) => {
		if (!file) return;
		const uploadType = UPLOAD_TYPES.find((t) => t.key === typeKey);
		const lower = file.name.toLowerCase();
		const valid = ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
		if (!valid) {
			showSnack("Please upload Excel (.xlsx/.xls) or CSV", "error");
			return;
		}

		try {
			const { rows, missing } = await parseFileForType(file, uploadType);
			updateState(typeKey, {
				file,
				rows,
				missing,
				uploaded: false,
				error: "",
			});
			setActivePreviewKey(typeKey);
			if (missing.length) {
				showSnack(`${uploadType.name}: missing ${missing.join(", ")}`, "warning");
			} else {
				showSnack(`${uploadType.name}: ${rows.length} rows ready`, "success");
			}
		} catch (err) {
			updateState(typeKey, {
				...emptyState(),
				error: err?.message || "Failed to read file",
			});
			showSnack(err?.message || "Failed to read file", "error");
		}
	};

	const handleClear = (typeKey) => {
		updateState(typeKey, emptyState());
	};

	const handleDownloadTemplate = (uploadType) => {
		const sheetData = [
			uploadType.columns.map((col) => col.label),
			uploadType.sample,
		];
		const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, uploadType.name);
		XLSX.writeFile(workbook, `mbo_lfs_${uploadType.key}_template.xlsx`);
	};

	const handleUpload = async (typeKey) => {
		const uploadType = UPLOAD_TYPES.find((t) => t.key === typeKey);
		const state = states[typeKey];
		if (!state.file || !state.rows.length) return;
		if (state.missing.length) {
			showSnack("Fix missing columns before upload", "error");
			return;
		}

		updateState(typeKey, { uploading: true, error: "" });
		try {
			const res = await pythonVmInstance.post("/mbo-lfs-qty-reco/upload", {
				type: typeKey,
				rows: state.rows.map((row) => ({
					customer_name: row.customer_name,
					location: row.location,
					date: row.date,
					ean_code: row.ean_code,
					qty: row.qty,
					document_no: row.document_no || "",
				})),
			});
			updateState(typeKey, {
				uploading: false,
				uploaded: true,
				file: null,
				rows: [],
				missing: [],
				error: "",
			});
			const stored = res?.data?.result?.rows ?? state.rows.length;
			showSnack(`${uploadType.name}: ${stored} rows stored`, "success");
			loadLatestUploads();
		} catch (err) {
			const data = err?.response?.data;
			const message =
				(typeof data === "string" && data.trim()) ||
				data?.message ||
				err?.message ||
				"Upload failed";
			updateState(typeKey, { uploading: false, uploaded: false, error: message });
			showSnack(message, "error");
		}
	};

	const activeType = UPLOAD_TYPES.find((t) => t.key === activePreviewKey) || UPLOAD_TYPES[0];
	const activeState = states[activePreviewKey] || emptyState();
	const previewRows = useMemo(
		() => (activeState.rows || []).slice(0, MAX_PREVIEW_ROWS),
		[activeState.rows]
	);

	const uploadedCount = UPLOAD_TYPES.filter((t) => states[t.key]?.uploaded).length;

	return (
		<Box>
			<Panel sx={{ px: 2, py: 1.5, mb: 1.5 }}>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					justifyContent="space-between"
					alignItems={{ xs: "flex-start", sm: "center" }}
					spacing={1}
				>
					<Box>
						<Typography variant="subtitle1" fontWeight={700} color="#111827">
							Multi-file Upload
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Each data type has its own columns — upload CUS-SOH and GIT files separately.
						</Typography>
					</Box>
					<Chip
						label={`${uploadedCount} / ${UPLOAD_TYPES.length} uploaded`}
						size="small"
						sx={{ height: 26, fontWeight: 600, bgcolor: "#f3f4f6" }}
					/>
				</Stack>
			</Panel>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: {
						xs: "minmax(0, 1fr)",
						md: "repeat(2, minmax(0, 1fr))",
						xl: "repeat(3, minmax(0, 1fr))",
					},
					gap: 1.5,
					mb: 1.5,
					alignItems: "stretch",
				}}
			>
				{UPLOAD_TYPES.map((uploadType) => (
					<Box key={uploadType.key} sx={{ minWidth: 0 }}>
						<UploadCard
							uploadType={uploadType}
							state={states[uploadType.key]}
							lastUpload={latestUploads[uploadType.key]}
							onFile={handleFile}
							onClear={handleClear}
							onUpload={handleUpload}
							onDownloadTemplate={handleDownloadTemplate}
						/>
					</Box>
				))}
			</Box>

			<Panel sx={{ overflow: "hidden" }}>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					alignItems={{ xs: "stretch", sm: "center" }}
					justifyContent="space-between"
					spacing={1}
					sx={{ px: 2, py: 1.25, borderBottom: "1px solid #eef0f3" }}
				>
					<Typography variant="subtitle2" fontWeight={700} color="#111827">
						Preview
					</Typography>
					<Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
						{UPLOAD_TYPES.map((type) => (
							<Chip
								key={type.key}
								size="small"
								label={type.name}
								onClick={() => setActivePreviewKey(type.key)}
								sx={{
									height: 26,
									fontWeight: 600,
									fontSize: "0.7rem",
									cursor: "pointer",
									bgcolor: activePreviewKey === type.key ? "#111827" : "#f3f4f6",
									color: activePreviewKey === type.key ? "#fff" : "#374151",
									"&:hover": {
										bgcolor: activePreviewKey === type.key ? "#111827" : "#e5e7eb",
									},
								}}
							/>
						))}
					</Stack>
				</Stack>

				{previewRows.length === 0 ? (
					<Box sx={{ py: 5, textAlign: "center" }}>
						<Typography variant="body2" color="text.secondary">
							Select a file in {activeType.name} to preview rows here.
						</Typography>
					</Box>
				) : (
					<TableContainer sx={{ maxHeight: 360 }}>
						<Table stickyHeader size="small">
							<TableHead>
								<TableRow>
									{activeType.columns.map((col) => (
										<TableCell
											key={col.key}
											align={col.key === "qty" ? "right" : "left"}
											sx={{
												fontWeight: 700,
												fontSize: "0.68rem",
												bgcolor: "#fafafa",
												whiteSpace: "nowrap",
												color: "#374151",
											}}
										>
											{col.label}
										</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								{previewRows.map((row) => (
									<TableRow key={row.id} hover>
										{activeType.columns.map((col) => (
											<TableCell
												key={col.key}
												align={col.key === "qty" ? "right" : "left"}
												sx={{
													fontSize: "0.8125rem",
													whiteSpace: "nowrap",
													fontWeight: col.key === "customer_name" ? 600 : 500,
													fontVariantNumeric:
														col.key === "qty" ? "tabular-nums" : undefined,
												}}
											>
												{formatCell(col.key, row[col.key])}
											</TableCell>
										))}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Panel>
		</Box>
	);
}

export default Upload;
